import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import { Button } from '@/components/Button/Button';
import { DocumentTitle } from '@/components/DocumentTitle';
import { Input } from '@/components/Form/Input/Input';
import { Navbar } from '@/components/Navbar/Navbar';
import { Warning } from '@/components/Warning';
import { eStrings } from '@/constants/strings';
import { useUserStore } from '@/stores/user/useUserStore';

type FormValues = {
  email: string;
  password: string;
};

/**
 * Route
 */
export const Route = createFileRoute('/login_old')({
  component: OldLoginComponent,
});

/**
 * Route component
 */
function OldLoginComponent() {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const navigate = useNavigate();

  const loginOld = useUserStore((user) => user.loginOld);
  const userIsLoading = useUserStore((user) => user.userIsLoading);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>();

  const onSubmit: SubmitHandler<FormValues> = async ({ email, password }) => {
    try {
      await loginOld({ email, password });

      navigate({ to: '/', search: { p: 1 } });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (error.request.status === 401) {
        setErrorMessage(
          'Er ging iets mis bij het inloggen. Controleer het e-mailadres en wachtwoord'
        );
      } else
        setErrorMessage('Er is een fout opgetreden, probeer het later opnieuw');
    }
  };

  return (
    <>
      <DocumentTitle sections={[eStrings.LBL_DIGI_V, 'Inloggen']} />

      <Navbar />

      <div className="mt-10 flex items-center justify-center px-4">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="w-full max-w-[64rem] space-y-2"
        >
          <h1 className="style-h2-bold text-black">
            {eStrings.LBL_INLOGPAGINA_TITEL}
          </h1>

          <p className="style-h2 text-gray-700">
            {eStrings.TXT_INLOGPAGINA_SUBTITEL}
          </p>

          {errorMessage && (
            <Warning variant="error" size="large">
              <Warning.Title>Inloggen mislukt!</Warning.Title>
              <Warning.Description>{errorMessage}</Warning.Description>
            </Warning>
          )}

          <Input
            {...register('email', { required: 'Dit veld is verplicht' })}
            id="login-email"
            label="E-mailadres"
            type="email"
            error={errors.email}
            required
            placeholder="Vul je e-mailadres in"
          />

          <Input
            {...register('password', { required: 'Dit veld is verplicht' })}
            id="login-password"
            label="Wachtwoord"
            type="password"
            error={errors.password}
            required
            placeholder="Geef je wachtwoord op"
          />

          <Button
            id="login-submit"
            type="submit"
            state={userIsLoading ? 'loading' : undefined}
            size="fullWidth"
          >
            <span className="block w-full text-center">Inloggen</span>
          </Button>
        </form>
      </div>
    </>
  );
}
